import React, { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '../config';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import StaffDetail from './StaffDetail';
import './NotificationCenter.css';

function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [showStaffDetail, setShowStaffDetail] = useState(false);
  const userEnvironment = localStorage.getItem('environment') || 'production';
  const navigate = useNavigate();
  // Add state for pagination and display limit
  const [displayLimit, setDisplayLimit] = useState(5);
  const maxNotificationsInPanel = 10;
  // Add ref for notification panel
  const notificationPanelRef = useRef(null);
  const notificationBellRef = useRef(null);

  
  // Fetch notifications from the server
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/notifications');
      console.log('Fetched notifications:', response.data); // Debug log
      
      if (response.data) {
        // Parse the data field for each notification
        const parsedNotifications = response.data.map(notification => ({
          ...notification,
          data: typeof notification.data === 'string' 
            ? JSON.parse(notification.data) 
            : notification.data
            
        }));
        
        // Sort notifications by date (newest first)
        const sortedNotifications = parsedNotifications.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        console.log('Parsed notifications:', sortedNotifications); // Debug log
        setNotifications(sortedNotifications);
        
        // Count unread notifications
        const unread = sortedNotifications.filter(n => !n.read_at).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle clicks outside the notification panel
  const handleClickOutside = useCallback((event) => {
    if (
      isOpen && 
      notificationPanelRef.current && 
      !notificationPanelRef.current.contains(event.target) &&
      notificationBellRef.current &&
      !notificationBellRef.current.contains(event.target)
    ) {
      setIsOpen(false);
    }
  }, [isOpen]);

  // Add event listener for clicks outside
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Mark a notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(`/api/notifications/${notificationId}/read`);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read_at: new Date().toISOString() } 
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await axiosInstance.put('/api/notifications/read-all');
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          read_at: notification.read_at || new Date().toISOString()
        }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  // Format relative time (e.g., "2 hours ago")
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'LICENSE_STATUS_CHANGE':
        return '🔄';
      case 'LICENSE_EXPIRING':
        return '⏱️';
      case 'POINTS_CHANGE':
        return '🚫';
      case 'NEW_STAFF':
        return '👤';
      case 'CHECK_LIMIT_WARNING':
        return '⚠️';
      case 'SYSTEM_UPDATE':
        return '🔔';
      default:
        return '📌';
    }
  };

  // Get notification priority class
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return '';
    }
  };

  // Toggle notification center visibility
  const toggleNotificationCenter = (e) => {
    e.stopPropagation(); // Prevent immediate closing
    setIsOpen(!isOpen);
    // Reset display limit when opening
    if (!isOpen) {
      setDisplayLimit(5);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // First mark as read if unread
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    
    // Close the notification panel
    setIsOpen(false);
    
    // Navigate to the notifications page
    navigate('/notifications');
  };

  // Handle view staff details
  const handleViewStaff = (staffId, e) => {
    e.stopPropagation(); // Prevent notification from being marked as read and navigation
    setSelectedStaffId(staffId);
    setShowStaffDetail(true);
    setIsOpen(false); // Close notification panel when opening staff details
  };

  // Handle staff detail close
  const handleStaffDetailClose = () => {
    setShowStaffDetail(false);
    setSelectedStaffId(null);
    // Optionally refresh notifications after viewing staff details
    fetchNotifications();
  };

  // Load more notifications
  const loadMoreNotifications = (e) => {
    e.stopPropagation(); // Prevent panel from closing
    setDisplayLimit(prev => Math.min(prev + 5, notifications.length));
  };

  // Load notifications on component mount
  useEffect(() => {
    fetchNotifications();
    
    // Set up polling for new notifications (every 5 minutes)
    const intervalId = setInterval(fetchNotifications, 5 * 60 * 1000);
    
    // Listen for notification-created events
    const handleNewNotification = () => {
      fetchNotifications();
    };
    
    window.addEventListener('notification-created', handleNewNotification);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('notification-created', handleNewNotification);
    };
  }, [fetchNotifications]);

  // Render notification content based on type
  const renderNotificationContent = (notification) => {
    const { type, message, data } = notification;
    
    // If data is not properly parsed, use the message directly
    if (!data || typeof data !== 'object') {
      return <div className="notification-text">{message}</div>;
    }
    
    switch (type) {
      case 'LICENSE_STATUS_CHANGE':
        return (
          <div className="notification-text">
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name}</strong>'s license status changed from{' '}
            <span className={`status ${data.old_status?.toLowerCase()}`}>
              {data.old_status || 'Unknown'}
            </span> to{' '}
            <span className={`status ${data.new_status.toLowerCase()}`}>
              {data.new_status}
            </span>
          </div>
        );
        
      case 'LICENSE_EXPIRING':
        return (
          <div className="notification-text">
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name}</strong>'s license will expire in{' '}
            <strong>{data.days_remaining} days</strong> ({data.expiry_date})
          </div>
        );
        
      case 'POINTS_CHANGE':
        return (
          <div className="notification-text">
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name}</strong>'s penalty points changed from{' '}
            <span className={`points points-${data.old_points > 6 ? 'high' : data.old_points > 3 ? 'medium' : 'low'}`}>
              {data.old_points}
            </span> to{' '}
            <span className={`points points-${data.new_points > 6 ? 'high' : data.new_points > 3 ? 'medium' : 'low'}`}>
              {data.new_points}
            </span>
          </div>
        );
        
      case 'NEW_STAFF':
        return (
          <div className="notification-text">
            New driver <strong>{data.staff_name}</strong> has been added to your account
          </div>
        );
        
      case 'CHECK_LIMIT_WARNING':
        return (
          <div className="notification-text">
            You have used <strong>{data.used_checks}</strong> of your{' '}
            <strong>{data.total_checks}</strong> monthly license checks
          </div>
        );
        
      case 'SYSTEM_UPDATE':
        return (
          <div className="notification-text">
            <strong>System Update:</strong> {data.message}
          </div>
        );
        
      default:
        return <div className="notification-text">{message}</div>;
    }
  };

  // Get filtered notifications based on current view
  const getFilteredNotifications = () => {
    const filtered = notifications.filter(notification => showAll || !notification.read_at);
    return filtered.slice(0, displayLimit);
  };

  // Count total notifications after filtering
  const totalFilteredCount = notifications.filter(notification => showAll || !notification.read_at).length;

  return (
    <div className="notification-center-container">
      {/* Notification Bell Icon */}
      <div 
        className="notification-bell" 
        onClick={toggleNotificationCenter}
        ref={notificationBellRef}
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </div>
      
      {/* Notification Panel */}
      {isOpen && (
        <div className="notification-panel" ref={notificationPanelRef}>
          <div className="notification-header">
            <h3>Notifications</h3>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                fetchNotifications();
              }} 
              className="refresh-btn" 
              title="Refresh notifications"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="refresh-icon">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </button>
            {notifications.length > 0 && (
              <button 
                className="mark-all-read" 
                onClick={(e) => {
                  e.stopPropagation();
                  markAllAsRead();
                }}
                disabled={unreadCount === 0}
              >
                Mark all as read
              </button>
            )}
          </div>
          
          <div className="notification-filter">
            <button 
              className={!showAll ? 'active' : ''} 
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(false);
              }}
            >
              Unread ({unreadCount})
            </button>
            <button 
              className={showAll ? 'active' : ''} 
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(true);
              }}
            >
              All ({notifications.length})
            </button>
          </div>
          
          <div className="notifications-list" onClick={(e) => e.stopPropagation()}>
            {loading ? (
              <div className="notification-loading">Loading notifications...</div>
            ) : error ? (
              <div className="notification-error">{error}</div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">No notifications</div>
            ) : (
              <>
                {getFilteredNotifications().map(notification => (
                  <div 
                    key={notification.id} 
                    className={`notification-item ${notification.read_at ? 'read' : 'unread'} ${getPriorityClass(notification.priority)}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-message">
                        {renderNotificationContent(notification)}
                      </div>
                      <div className="notification-time">
                        {formatRelativeTime(notification.created_at)}
                      </div>
                    </div>
                    {notification.staff_id && (
                      <button 
                        className="notification-action"
                        onClick={(e) => handleViewStaff(notification.staff_id, e)}
                      >
                        View
                      </button>
                    )}
                  </div>
                ))}
                
                {/* Load more button */}
                {displayLimit < totalFilteredCount && (
                  <div className="load-more-container">
                    <button className="load-more-btn" onClick={loadMoreNotifications}>
                      Load more ({totalFilteredCount - displayLimit} remaining)
                    </button>
                  </div>
                )}
                
                {/* Show count summary if there are many notifications */}
                {totalFilteredCount > maxNotificationsInPanel && (
                  <div className="notification-count-summary">
                    Showing {Math.min(displayLimit, totalFilteredCount)} of {totalFilteredCount} notifications
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="notification-footer" onClick={(e) => e.stopPropagation()}>
            <Link to="/notifications" onClick={() => setIsOpen(false)}>
              View all notifications
            </Link>
          </div>
        </div>
      )}
      
      {/* Staff Detail Modal */}
      {showStaffDetail && selectedStaffId && createPortal(
        <StaffDetail
          staffId={selectedStaffId}
          token={localStorage.getItem('token')}
          environment={userEnvironment}
          onClose={handleStaffDetailClose}
        />,
        document.body
      )}
    </div>
  );
}

export default NotificationCenter; 